





import Vue from 'vue'
import { mapGetters } from 'vuex'
import models from '@/mixins/models-mixin.js'
import preview from '@/mixins/message-mixin'
import Group from '@/types/Group.interface'
import Broadcast, { BroadcastTemplate } from '@/types/Broadcast.interface'
import BroadcastForm from '@/components/BroadcastForm.vue'
export default Vue.extend({
    mixins: [models, preview],
    components: {
        BroadcastForm
    },
    data: () => ({
        broadcast: null,
        formClick: null,
        toolbar: {
            title: 'New Broadcast',
            left: [
                {
                    title: 'Back',
                    icon: 'mdi-chevron-left',
                    back: '/broadcasts',
                    tooltip: 'Back',
                    permission: 0,
                    feature: null
                }
            ],
            right: []
        },
        navigateAwayPath: null
    }),
    async mounted() {
        //  Grab our opt out group
        await this.$store.dispatch('groupsOptOut')

        //  Get our preseeded persons for our selectors
        await this.$store.dispatch('personsPreseeded')

        //  Init our broadcast
        this.init()
    },
    beforeRouteLeave(to, from, next) {
        //  Unset our hint
        this.$store.commit('broadcastsViewMediaHintSet', false)

        //  Unset our contact count
        this.$store.commit('broadcastsViewSelectedBroadcastTotalCountSet', 0)

        //  Check if our broadcast edited
        const edited = this.$refs.broadcastForm?.broadcastEdited

        //  If user came from form click, let user proceed to next route
        if (this.formClick || !edited) return next()
        //  Else if navigatepath not set
        else if (!this.navigateAwayPath) {
            //  Set path
            this.navigateAwayPath = to.path

            //  Get our broadcast form ref
            const ref = this.$refs.broadcastForm

            //  If the ref doesn't exist, do nothing
            if (!ref) return

            //  Pop save draft pop up
            return ref.popSaveDraft(to.path)
        }
        next()
    },
    computed: {
        ...mapGetters({
            selectedBroadcast: 'broadcastsViewSelectedBroadcast'
        }),
        broadcastsEnabled(): boolean {
            return this.$store.getters.getEnabled('broadcast')
        },
        optOutGroup(): Group {
            return this.$store.getters.optOutGroup
        },
        selectedGroups: {
            get(): Group {
                return this.$store.getters.broadcastsViewSelectedGroups
            },
            set(val: Group): void {
                this.$store.commit('broadcastsViewSelectedGroupsSet', val)
            }
        },
        selectedTemplate(): BroadcastTemplate {
            return this.$store.getters.broadcastsViewSelectedTemplate
        }
    },
    methods: {
        applyTemplate(): void {
            //  Check if we have a template in use
            const template = this.selectedTemplate

            //  If we don't have a template then do nothing
            if (!template) return

            //  Check if we need to signal to the user about media
            const media = template.media

            //  Create our message by grabbing template's message and appending media info as needed
            const msg = media
                ? template.message +
                  '\n(Click below: ⬇️ to add an image to your message!)'
                : template.message

            //  If we do, then apply the template
            this.broadcast.message = msg

            //  We applied our template so make sure we unset it
            this.$store.commit('broadcastsViewSelectedTemplateSet', null)

            //  Make some toast appear
            setTimeout(() => {
                this.$store.commit(
                    'appToastSet',
                    {
                        text: 'Template Applied!',
                        visible: true
                    },
                    1000
                )
            })

            //  If not, then we're done
            if (!media) return

            //  Else, let's give a little hint
            this.$store.commit('broadcastsViewMediaHintSet', true)
        },
        //  Copies an existing broadcast to our local broadcast var
        async dupeBroadcast(id: number): Promise<void> {
            //  Check if we have a selected broadcast
            const existing = this.selectedBroadcast

            //  Check that the broadcast matches our provided id
            const match = existing?.id == id

            //  If they don't match then we need to get our broadcast from DB
            if (!match)
                await this.$store.dispatch('broadcastsViewSelectedBroadcastGet', id)

            //  Grab our selected broadcast
            var broadcast = this.selectedBroadcast

            //  Grab all the associated data
            const broadcastData = await this.$store.dispatch('draftGet', broadcast.id)

            //  Set the data to the broadcast object
            broadcast.groups = broadcastData.groups
            broadcast.persons = broadcastData.persons
            broadcast.exclude_persons = broadcastData.excludePersons
            broadcast.exclude_groups = broadcastData.excludeGroups

            //  Copy this broadcast to our local
            this.broadcast = window._.cloneDeep(broadcast)

            //  Remove schedules, we don't want to copy schedules
            delete this.broadcast.schedule
            delete this.broadcast.schedules
        },
        async init(): Promise<void> {
            //  Grab our copy param from the route query
            const copy = this.$route.query.copy

            //  If copy is set then we will just copy our selected broadcast
            if (copy) return this.dupeBroadcast(copy)

            //  Else, create a new broadcast
            this.broadcast = await this.newBroadcast()

            //  Apply a template if we have one
            this.applyTemplate()

            // Set our groups to null
            this.selectedGroups = null
        },
        async newBroadcast(): Promise<Broadcast> {
            //  Grab group query param
            const id = parseInt(this.$route.query.group)

            //  If it exists, grab group
            if (id) await this.$store.dispatch('broadcastsViewSelectedGroupsGet', [id])

            //  Grab our selected group
            const groups = this.selectedGroups

            //  Get our opt out group
            const opt = this.optOutGroup

            return {
                object: 'Broadcast',
                name: null,
                group_ids: id ? [id] : [],
                groups: groups ? groups : [],
                person_ids: [],
                persons: [],
                exclude_group_ids: [opt.id],
                exclude_groups: [opt],
                exclude_person_ids: [],
                exclude_persons: [],
                message: null,
                schedules: null
            }
        },
        //  Method to handle emit event coming from broadcast form
        routeNext(): void {
            //  If path present, route to it
            if (this.navigateAwayPath) this.$router.push(this.navigateAwayPath)
        }
    },
    watch: {
        async $route(to, from): Promise<void> {
            //  If the 'group' query doesn't exist, we should just unset our selected group
            if (!to.query.group) return (this.selectedGroups = null)

            //  Else, grab the id from the query
            const id = to.query.group

            //  Grab our selected group
            const existing = this.selectedGroups

            //  Check if our currently selected group matches this id
            const match = existing.id == id

            //  If they do NOT match we have to get the correct group
            if (!match)
                await this.$store.dispatch('broadcastsViewSelectedGroupsGet', [id])

            //  Grab our selected group (regardless of if we had to fetch or not, this is now bound to the right group)
            const group = this.selectedGroups

            //  Bind the group to our broadcast
            this.broadcast.group = group
            this.broadcast.group_id = group.id
        }
    }
})
